import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { HookStampButton } from "./HookStampButton";
import { HookType } from "./hookInputSchemaStamp.helpers";
import "./HookFieldActions.scss";

// Docs anchors surfaced as the two "?" links around the hook row. The first
// explains the lifecycle-hook flows themselves (the dropdown), the second the
// one-click input-schema stamping (the button).
const HOOK_FLOWS_DOCS_PATH = "/docs/object-templates-custom-objects#hooks";
const HOOK_STAMP_DOCS_PATH = "/docs/object-templates-custom-objects#stamping-the-hook-input-schema";

const STAMP_DOCS_TEXT =
    "Generate the matching input schema for the selected hook flow — typed field-by-field from " +
    "this object template's attributes — and write it onto the flow (you confirm against a diff first).";

interface HookFieldActionsProps {
    hookType: HookType;
    /** The hook field's label (e.g. "On create") — used as the docs tooltip title. */
    fieldLabel: string;
    /** Plain-language explanation of this lifecycle-hook flow, shown on the dropdown "?". */
    flowDocsText: string;
    /** The saved object template id (stamping operates on the saved record). */
    objectTemplateId?: string;
    /** Enable the stamp button only when a flow is bound to this hook. */
    flowSelected: boolean;
    /** Follow the field's read-only/disabled state. */
    disabled?: boolean;
    /** Called after a successful stamp so the caller can refetch the record. */
    fetchResourceData?: (() => any) | null;
}

/**
 * Action cluster rendered to the right of a lifecycle-hook flow picker on the
 * object template screen. Left-to-right:
 *   [? hook-flow docs]  [Stamp input schema button]  [? schema-stamping docs]
 * The first "?" sits next to the dropdown it explains (the lifecycle-hook flows);
 * the second sits next to the Stamp button and explains input-schema stamping.
 * Spacing from the dropdown and between the items follows the 8px spacing rhythm
 * (ui-design-principle-spacing-layout). See
 * operator-task-fe-ot-lifecycle-stamp-schema-spacing-and-docs-links (Margot, 2026-09-30).
 */
export const HookFieldActions = ({
    hookType,
    fieldLabel,
    flowDocsText,
    objectTemplateId,
    flowSelected,
    disabled,
    fetchResourceData,
}: HookFieldActionsProps) => (
    <div className="hookFieldActions">
        <DocsInfoTooltip title={fieldLabel} text={flowDocsText} urlPath={HOOK_FLOWS_DOCS_PATH} />
        <HookStampButton
            hookType={hookType}
            objectTemplateId={objectTemplateId}
            flowSelected={flowSelected}
            disabled={disabled}
            fetchResourceData={fetchResourceData}
        />
        <DocsInfoTooltip
            title="Stamp input schema"
            text={STAMP_DOCS_TEXT}
            urlPath={HOOK_STAMP_DOCS_PATH}
        />
    </div>
);
